import { Box, Button, Group, Popover, Stack, Text, Textarea, TextInput, UnstyledButton } from '@mantine/core';
import { IconArrowUpRight } from '@tabler/icons-react';
import { CSSProperties, ReactElement, ReactNode, useState } from 'react';
import { ORBIT_GRAY, ORBIT_NEEDS_ACTION, ORBIT_SETTLED } from '@/theme/colors';
import { formatLedgerMoney, LedgerRow, sourceLabel } from '../ledger/ledgerRows';
import '../ledger/ledger.css';
import { formatShortDate, linkCandidates, parseAmount, requestType, ROW_BUTTON_STYLE } from './refundRequests';
import { RefundRequestRow } from './useRefundWorklists';

/** The Refunds view's two row actions (BAR-3033): Approve, then Link refund. */

const WHY_STYLE: CSSProperties = {
  fontSize: 10.5,
  lineHeight: '14px',
  fontWeight: 600,
  padding: '1px 6px',
  borderRadius: 999,
  background: 'rgba(31, 122, 68, 0.10)',
  color: ORBIT_SETTLED,
  whiteSpace: 'nowrap',
};

const OUTFLOW_STYLE: CSSProperties = {
  width: 22,
  height: 22,
  borderRadius: 999,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  background: 'rgba(138, 90, 18, 0.12)',
  color: ORBIT_NEEDS_ACTION,
};

const GROUP_HEADING_STYLE: CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: 'rgba(0, 0, 0, 0.35)',
  textTransform: 'uppercase',
  letterSpacing: 0.4,
};

function RowPopover({
  label,
  width,
  children,
}: {
  label: string;
  width: number;
  children: (close: () => void) => ReactNode;
}): ReactElement {
  const [opened, setOpened] = useState(false);
  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="bottom-end"
      width={width}
      withinPortal
      shadow="md"
      transitionProps={{ duration: 0 }}
    >
      <Popover.Target>
        <UnstyledButton style={ROW_BUTTON_STYLE} onClick={() => setOpened((open) => !open)}>
          {label}
        </UnstyledButton>
      </Popover.Target>
      <Popover.Dropdown>{opened && children(() => setOpened(false))}</Popover.Dropdown>
    </Popover>
  );
}

export function ApprovePopover({
  request,
  onApprove,
}: {
  request: RefundRequestRow;
  onApprove: (amount: number, note: string) => void;
}): ReactElement {
  return (
    <RowPopover label="Approve" width={300}>
      {(close) => (
        <ApproveForm
          request={request}
          onCancel={close}
          onApprove={(amount, note) => {
            close();
            onApprove(amount, note);
          }}
        />
      )}
    </RowPopover>
  );
}

function ApproveForm({
  request,
  onCancel,
  onApprove,
}: {
  request: RefundRequestRow;
  onCancel: () => void;
  onApprove: (amount: number, note: string) => void;
}): ReactElement {
  const [amount, setAmount] = useState(request.amountRequested.toFixed(2));
  const [note, setNote] = useState('');
  return (
    <Stack gap={10}>
      <Text fz={13} fw={600}>
        {`Approve ${requestType(request).toLowerCase()} for ${request.patientDisplayName}`}
      </Text>
      <TextInput size="xs" label="Amount requested" value={request.amountRequested.toFixed(2)} readOnly />
      <TextInput
        size="xs"
        label="Amount approved"
        inputMode="decimal"
        value={amount}
        onChange={(event) => setAmount(event.currentTarget.value)}
        data-autofocus
      />
      <Textarea
        size="xs"
        label="Note"
        autosize
        minRows={2}
        value={note}
        onChange={(e) => setNote(e.currentTarget.value)}
      />
      <Group justify="flex-end" gap={8}>
        <Button size="xs" variant="default" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          size="xs"
          color="dark"
          disabled={parseAmount(amount) <= 0}
          onClick={() => onApprove(parseAmount(amount), note.trim())}
        >
          Approve
        </Button>
      </Group>
    </Stack>
  );
}

export function LinkRefundPopover({
  request,
  refunds,
  onLink,
}: {
  request: RefundRequestRow;
  refunds: LedgerRow[];
  onLink: (refund: LedgerRow) => void;
}): ReactElement {
  return (
    <RowPopover label="Link refund" width={340}>
      {(close) => {
        const candidates = linkCandidates(request, refunds);
        return (
          <Stack gap={6}>
            <Text fz={13} fw={600}>
              {`Link a refund to ${request.patientDisplayName}`}
            </Text>
            <Text style={GROUP_HEADING_STYLE}>Refunds with no request</Text>
            {candidates.length === 0 ? (
              <Text fz={13} c="rgba(0, 0, 0, 0.35)" ta="center" py={14} px={8}>
                No unlinked refunds yet. It links itself when the refund arrives.
              </Text>
            ) : (
              candidates.map(({ refund, why }) => (
                <UnstyledButton
                  key={`${refund.record}:${refund.id}`}
                  className="ledger-connect-option"
                  style={{ display: 'flex', alignItems: 'flex-start', gap: 9, padding: '7px 8px', borderRadius: 8 }}
                  onClick={() => {
                    close();
                    onLink(refund);
                  }}
                >
                  <span style={OUTFLOW_STYLE}>
                    <IconArrowUpRight size={13} stroke={2} />
                  </span>
                  <Box>
                    <Text fz={13} fw={600}>
                      {`${formatLedgerMoney(refund.amount)} · ${formatShortDate(refund.occurredAt)}`}
                    </Text>
                    <Group gap={5} mt={1}>
                      <Text fz={12} c={ORBIT_GRAY}>
                        {`${sourceLabel(refund.source)} · ${refund.patientDisplayName ?? refund.payerName ?? ''}`}
                      </Text>
                      {why.map((reason) => (
                        <span key={reason} style={WHY_STYLE}>
                          {reason}
                        </span>
                      ))}
                    </Group>
                  </Box>
                </UnstyledButton>
              ))
            )}
          </Stack>
        );
      }}
    </RowPopover>
  );
}
